import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { X, Megaphone, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const DISMISS_KEY_PREFIX = "jb_dismissed_announcements";

function dismissKey(userId) {
  return userId ? `${DISMISS_KEY_PREFIX}:${userId}` : DISMISS_KEY_PREFIX;
}

function loadDismissed(userId) {
  try { return JSON.parse(localStorage.getItem(dismissKey(userId)) || "[]"); } catch { return []; }
}
function saveDismissed(userId, ids) {
  try { localStorage.setItem(dismissKey(userId), JSON.stringify(ids)); } catch {}
}

export default function BulletinBoard({ currentUser }) {
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState(() => loadDismissed(currentUser?.id));
  const [manageOpen, setManageOpen] = useState(false);
  const isAdmin = currentUser?.role === "admin";

  const load = async () => {
    try {
      const { data } = await api.get("/announcements/active");
      setItems(data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const visible = items.filter((a) => !dismissed.includes(a.id));

  const dismiss = (id) => {
    const next = [...dismissed, id];
    setDismissed(next);
    saveDismissed(currentUser?.id, next);
  };

  if (visible.length === 0 && !isAdmin) return null;

  return (
    <div className="space-y-2" data-testid="bulletin-board">
      {visible.map((a) => (
        <div
          key={a.id}
          className="flex items-start gap-3 border border-[#B85C38]/30 bg-[#FFF7F1] px-4 py-3 rounded-sm"
          data-testid={`announcement-${a.id}`}
        >
          <Megaphone className="h-4 w-4 text-[#B85C38] mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-stone-900">{a.title}</div>
            {a.body && (
              <div className="text-sm text-stone-700 whitespace-pre-wrap mt-0.5">{a.body}</div>
            )}
            <div className="text-[10px] font-mono uppercase tracking-wider text-stone-500 mt-1">
              posted by {a.author_name}
            </div>
          </div>
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setManageOpen(true)}
              className="h-8 rounded-sm border-[#B85C38] text-[#B85C38] hover:bg-[#B85C38] hover:text-white text-xs font-medium shrink-0"
              data-testid="manage-announcements-btn"
            >
              <Plus className="h-3.5 w-3.5 mr-1" /> Manage
            </Button>
          )}
          <button
            onClick={() => dismiss(a.id)}
            className="text-stone-400 hover:text-stone-700 transition-colors"
            data-testid={`dismiss-${a.id}`}
            title="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
      {isAdmin && visible.length === 0 && (
        <div
          className="flex items-center gap-3 border border-dashed border-stone-300 bg-stone-50 px-4 py-3 rounded-sm"
          data-testid="bulletin-empty-admin"
        >
          <Megaphone className="h-4 w-4 text-stone-400 flex-shrink-0" />
          <div className="text-sm text-stone-600 flex-1">
            No active announcements. Post one to reach the whole board.
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setManageOpen(true)}
            className="h-8 rounded-sm border-[#B85C38] text-[#B85C38] hover:bg-[#B85C38] hover:text-white text-xs font-medium shrink-0"
            data-testid="manage-announcements-btn"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> Post announcement
          </Button>
        </div>
      )}
      {manageOpen && (
        <ManageAnnouncements
          onClose={() => setManageOpen(false)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function ManageAnnouncements({ onClose, onChanged }) {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/announcements");
      setItems(data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      await api.post("/announcements", { title: title.trim(), body: body.trim(), active: true });
      toast.success("Announcement posted");
      setTitle(""); setBody("");
      load(); onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const toggleActive = async (a) => {
    try {
      await api.patch(`/announcements/${a.id}`, { title: a.title, body: a.body, active: !a.active });
      load(); onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this announcement?")) return;
    try {
      await api.delete(`/announcements/${id}`);
      load(); onChanged();
    } catch {}
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="manage-announcements-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Head Coach's Whiteboard</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2 border-b border-stone-200 pb-4">
          <Input
            placeholder="Title (e.g. Board meeting Thursday 7pm)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="h-10 rounded-sm border-stone-300"
            data-testid="ann-title-input"
          />
          <Textarea
            placeholder="Details (optional)"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="rounded-sm border-stone-300 min-h-[80px]"
            data-testid="ann-body-input"
          />
          <Button
            onClick={submit}
            disabled={busy || !title.trim()}
            className="rounded-sm bg-[#B85C38] hover:bg-[#9A4D2E] text-white"
            data-testid="ann-post-btn"
          >
            Post
          </Button>
        </div>

        <div className="space-y-2 py-2">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-stone-500 mb-1">
            Existing announcements
          </div>
          {items.length === 0 && (
            <div className="text-sm text-stone-500 italic">None yet.</div>
          )}
          {items.map((a) => (
            <div key={a.id} className="border border-stone-200 p-3 rounded-sm flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-medium ${a.active ? "text-stone-900" : "text-stone-400 line-through"}`}>
                  {a.title}
                </div>
                {a.body && <div className="text-xs text-stone-600 mt-0.5 whitespace-pre-wrap">{a.body}</div>}
              </div>
              <Button variant="outline" size="sm" onClick={() => toggleActive(a)} className="h-7 rounded-sm border-stone-300 text-xs">
                {a.active ? "Hide" : "Show"}
              </Button>
              <Button variant="outline" size="sm" onClick={() => remove(a.id)} className="h-7 rounded-sm border-red-200 text-red-700 text-xs">
                Delete
              </Button>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm border-stone-300">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
